if (!customElements.get('collection-infinite-scroll')) {
  const STORAGE_PREFIX = 'skafe:collection-nav:';
  const STATE_VERSION = 1;
  const MAX_RESTORE_PAGES = 25;
  const STATE_MAX_AGE_MS = 1000 * 60 * 60 * 2;
  const PAGE_ROOT_MARGIN = '-40% 0px -45% 0px';
  const SENTINEL_ROOT_MARGIN = '600px 0px';

  const stringsFrom = (el) => ({
    loading: el.dataset.i18nLoading || 'Loading more products',
    loaded: el.dataset.i18nLoaded || '{{ count }} more products loaded',
    end: el.dataset.i18nEnd || 'You have reached the end of the collection',
    error:
      el.dataset.i18nError ||
      'More products could not be loaded. Use the pagination links to continue.',
  });

  const normalizeCollectionQuery = (search) => {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(raw);
    params.delete('page');
    params.delete('section_id');
    const normalized = new URLSearchParams();
    for (const key of [...params.keys()].sort()) {
      for (const value of params.getAll(key).sort()) normalized.append(key, value);
    }
    return normalized.toString();
  };

  const buildCollectionPageUrl = (pathname, search, page) => {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(raw);
    params.delete('section_id');
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const storageKeyFor = (collectionUrl, normalizedQuery) =>
    `${STORAGE_PREFIX}${collectionUrl}?${normalizedQuery}`;

  const productCardKey = (card) => {
    const id = card.getAttribute('data-product-id');
    const handle = card.getAttribute('data-product-handle');
    if (id) return `id:${id}`;
    if (handle) return `handle:${handle}`;
    return null;
  };

  const prefersReducedMotion = () =>
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  const isModifiedClick = (event) =>
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

  const readNextUrl = (root) =>
    root.querySelector('[data-infinite-next]')?.getAttribute('href') ||
    root.dataset.nextUrl ||
    null;

  customElements.define(
    'collection-infinite-scroll',
    class CollectionInfiniteScroll extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        this.grid = this.querySelector('[data-product-grid]');
        this.nextLink = this.querySelector('[data-infinite-next]');
        this.status = this.querySelector('[data-infinite-status]');
        this.sentinel = this.querySelector('[data-infinite-sentinel]');
        this.sectionId = this.dataset.sectionId;
        this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
        this.totalPages = Number(this.dataset.totalPages) || 1;
        this.currentPage = Number(this.dataset.currentPage) || 1;
        this.loadedPages = new Set([this.currentPage]);
        this.nextUrl = readNextUrl(this);
        this.pageCache = new Map();
        this.loading = false;
        this.restoring = false;
        this.controller = null;
        this.logicalPage = this.currentPage;
        this.i18n = stringsFrom(this);
        this._onProductClick = this.onProductClick.bind(this);
        this._onPageShow = this.onPageShow.bind(this);
        this._onPageHide = this.onPageHide.bind(this);

        if (!this.grid || this.totalPages <= 1) return;

        if ('scrollRestoration' in history && !window.Shopify?.designMode) {
          history.scrollRestoration = 'manual';
        }

        this.grid.addEventListener('click', this._onProductClick);
        window.addEventListener('pageshow', this._onPageShow);
        window.addEventListener('pagehide', this._onPageHide);

        this.observeSentinel();
        this.observePageMarkers();
        this.syncUrl(this.logicalPage);

        if (!window.Shopify?.designMode) {
          this.maybeRestore();
        }
      }

      disconnectedCallback() {
        this.abortActive();
        this.sentinelObserver?.disconnect();
        this.pageObserver?.disconnect();
        this.grid?.removeEventListener('click', this._onProductClick);
        window.removeEventListener('pageshow', this._onPageShow);
        window.removeEventListener('pagehide', this._onPageHide);
        this._initialized = false;
      }

      onPageShow(event) {
        if (event.persisted) {
          this._bfcacheRestored = true;
          return;
        }
        if (!window.Shopify?.designMode) {
          this.maybeRestore();
        }
      }

      onPageHide() {
        if (window.Shopify?.designMode || this.restoring) return;
        const existing = this.readSavedState();
        const focused = this.grid?.querySelector('[data-product-card]:focus-within') || null;
        this.saveState({
          focusedProductId:
            existing?.focusedProductId || focused?.getAttribute('data-product-id') || null,
          focusedProductHandle:
            existing?.focusedProductHandle || focused?.getAttribute('data-product-handle') || null,
          scrollY: window.scrollY,
        });
      }

      observeSentinel() {
        this.sentinelObserver?.disconnect();
        if (!this.sentinel || !this.nextUrl) return;
        if (!('IntersectionObserver' in window)) {
          this.loadNextPage({ reason: 'fallback' });
          return;
        }
        this.sentinelObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage({ reason: 'sentinel' });
          },
          { rootMargin: SENTINEL_ROOT_MARGIN }
        );
        this.sentinelObserver.observe(this.sentinel);
      }

      observePageMarkers() {
        this.pageObserver?.disconnect();
        if (!('IntersectionObserver' in window)) return;
        this.pageObserver = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (!visible.length) return;
            const page = Number(visible[0].target.getAttribute('data-page'));
            if (!page || page === this.logicalPage) return;
            this.logicalPage = page;
            this.syncUrl(page);
          },
          { rootMargin: PAGE_ROOT_MARGIN, threshold: 0 }
        );
        this.grid.querySelectorAll('[data-product-card][data-page]').forEach((card, index, all) => {
          const page = card.getAttribute('data-page');
          const prev = index > 0 ? all[index - 1].getAttribute('data-page') : null;
          if (page !== prev) this.pageObserver.observe(card);
        });
      }

      syncUrl(page) {
        if (window.Shopify?.designMode) return;
        const url = buildCollectionPageUrl(window.location.pathname, window.location.search, page);
        const current = `${window.location.pathname}${window.location.search}`;
        if (url === current) return;
        const prevState = history.state && typeof history.state === 'object' ? history.state : {};
        const searchParams =
          prevState.searchParams ??
          (typeof FacetFiltersForm !== 'undefined'
            ? FacetFiltersForm.searchParamsPrev
            : normalizeCollectionQuery(window.location.search));
        history.replaceState({ ...prevState, searchParams, collectionPage: page }, '', url);
      }

      setLoading(isLoading, { skeletons = true } = {}) {
        this.loading = isLoading;
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.classList.toggle('is-loading', isLoading);
        if (isLoading) {
          this.announce(this.i18n.loading);
          if (skeletons) this.showSkeletons();
        } else {
          this.clearSkeletons();
        }
      }

      skeletonCount() {
        const className = this.grid?.className || '';
        const desktop = Number(/grid--(\d+)-col-desktop/.exec(className)?.[1] || 3);
        const mobile = Number(/grid--(\d+)-col-tablet-down/.exec(className)?.[1] || 2);
        const cols = window.matchMedia('(min-width: 750px)').matches ? desktop : mobile;
        return Math.max(2, Math.min(cols * 2, 8));
      }

      showSkeletons() {
        if (!this.grid || this.querySelector('[data-infinite-skeleton]')) return;
        const fragment = document.createDocumentFragment();
        const count = this.skeletonCount();
        for (let index = 0; index < count; index += 1) {
          const item = document.createElement('li');
          item.className = 'grid__item collection-infinite-scroll__skeleton';
          item.setAttribute('data-infinite-skeleton', '');
          item.setAttribute('aria-hidden', 'true');
          item.innerHTML = `
            <div class="collection-infinite-scroll__skeleton-card">
              <div class="collection-infinite-scroll__skeleton-media"></div>
              <div class="collection-infinite-scroll__skeleton-lines">
                <span class="collection-infinite-scroll__skeleton-line"></span>
                <span class="collection-infinite-scroll__skeleton-line collection-infinite-scroll__skeleton-line--short"></span>
                <span class="collection-infinite-scroll__skeleton-line collection-infinite-scroll__skeleton-line--price"></span>
              </div>
            </div>
          `;
          fragment.appendChild(item);
        }
        this.grid.appendChild(fragment);
      }

      clearSkeletons() {
        this.grid?.querySelectorAll('[data-infinite-skeleton]').forEach((node) => node.remove());
      }

      announce(message) {
        if (!this.status || !message) return;
        this.status.textContent = '';
        requestAnimationFrame(() => {
          this.status.textContent = message;
        });
      }

      onProductClick(event) {
        if (window.Shopify?.designMode || isModifiedClick(event)) return;
        const link = event.target.closest('a[href]');
        if (!link || !this.grid.contains(link)) return;
        if (link.closest('modal-opener, quick-add-modal, product-form')) return;
        if (link.getAttribute('target') === '_blank') return;
        const card = link.closest('[data-product-card]');
        if (!card) return;
        const href = link.getAttribute('href') || '';
        if (!href.includes('/products/')) return;
        this.saveState({
          focusedProductId: card.getAttribute('data-product-id'),
          focusedProductHandle: card.getAttribute('data-product-handle'),
          scrollY: window.scrollY,
        });
      }

      existingKeys() {
        return new Set(
          [...this.grid.querySelectorAll('[data-product-card]')]
            .map(productCardKey)
            .filter(Boolean)
        );
      }

      updateNextUrl(url) {
        this.nextUrl = url;
        if (this.nextLink) {
          if (url) {
            this.nextLink.setAttribute('href', url);
            this.nextLink.hidden = true;
          } else {
            this.nextLink.removeAttribute('href');
          }
        }
      }

      async loadNextPage({ reason = 'sentinel', pageUrl = null } = {}) {
        const url = pageUrl || this.nextUrl;
        if (!url || this.loading) return false;
        if (this.restoring && reason !== 'restore') return false;
        const pageNumber = Number(new URL(url, window.location.origin).searchParams.get('page') || 0);
        if (pageNumber && this.loadedPages.has(pageNumber)) {
          if (!pageUrl) {
            this.updateNextUrl(null);
            this.finishCollection();
          }
          return false;
        }

        this.setLoading(true, { skeletons: reason !== 'restore' });
        this.controller = new AbortController();
        try {
          const cards = await this.fetchPageCards(url, this.controller.signal);
          if (!cards) throw new Error('empty page');
          this.clearSkeletons();
          const appended = this.appendCards(cards.elements, cards.page, { animate: reason !== 'restore' });
          this.loadedPages.add(cards.page);
          this.updateNextUrl(cards.nextUrl);
          this.observePageMarkers();
          if (!this.nextUrl) this.finishCollection();
          else {
            this.observeSentinel();
            if (appended > 0 && reason !== 'restore') {
              this.announce(this.i18n.loaded.replace('{{ count }}', String(appended)));
            }
          }
          document.dispatchEvent(
            new CustomEvent('collection:products-loaded', {
              bubbles: true,
              detail: { page: cards.page, count: appended, reason },
            })
          );
          return true;
        } catch (error) {
          if (error?.name === 'AbortError') return false;
          console.warn('[collection-infinite-scroll] failed to load page', error);
          this.clearSkeletons();
          this.announce(this.i18n.error);
          return false;
        } finally {
          this.controller = null;
          this.setLoading(false, { skeletons: false });
        }
      }

      async fetchPageCards(pageUrl, signal) {
        const absolute = new URL(pageUrl, window.location.origin);
        const page = Number(absolute.searchParams.get('page') || 1);
        if (this.pageCache.has(page)) return this.pageCache.get(page);

        const requestUrl = new URL(absolute);
        if (this.sectionId) requestUrl.searchParams.set('section_id', this.sectionId);

        const response = await fetch(requestUrl.toString(), { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const remoteRoot =
          doc.querySelector('[data-infinite-collection]') ||
          doc.querySelector('[data-product-grid]')?.closest('.collection') ||
          doc;
        const remoteGrid = doc.querySelector('[data-product-grid]') || doc.getElementById('product-grid');
        if (!remoteGrid) throw new Error('missing grid');
        const elements = [...remoteGrid.querySelectorAll(':scope > [data-product-card], :scope > .grid__item')];
        elements.forEach((el) => {
          if (!el.hasAttribute('data-product-card')) el.setAttribute('data-product-card', '');
          if (!el.getAttribute('data-page')) el.setAttribute('data-page', String(page));
        });
        const nextUrl = readNextUrl(remoteRoot instanceof Element ? remoteRoot : doc.body);
        const payload = { page, elements, nextUrl };
        this.pageCache.set(page, payload);
        return payload;
      }

      appendCards(elements, page, { animate = true } = {}) {
        const existing = this.existingKeys();
        const fragment = document.createDocumentFragment();
        const reduceMotion = prefersReducedMotion();
        let appended = 0;
        for (const node of elements) {
          const card = node.cloneNode(true);
          card.setAttribute('data-page', String(page));
          card.classList.add('scroll-trigger--cancel');
          if (animate && !reduceMotion) {
            card.classList.add('collection-infinite-scroll__card-enter');
            card.style.animationDelay = `${Math.min(appended, 8) * 45}ms`;
          }
          const key = productCardKey(card);
          if (key && existing.has(key)) continue;
          if (key) existing.add(key);
          fragment.appendChild(card);
          appended += 1;
        }
        this.grid.appendChild(fragment);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
        return appended;
      }

      abortActive() {
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }
        this.clearSkeletons();
        this.setLoading(false, { skeletons: false });
      }

      finishCollection() {
        this.sentinelObserver?.disconnect();
        this.updateNextUrl(null);
        this.announce(this.i18n.end);
      }

      saveState({ focusedProductId = null, focusedProductHandle = null, scrollY = window.scrollY } = {}) {
        if (window.Shopify?.designMode) return;
        const normalizedQuery = normalizeCollectionQuery(window.location.search);
        const state = {
          version: STATE_VERSION,
          collectionUrl: this.collectionUrl,
          normalizedQuery,
          currentPage: this.logicalPage,
          loadedPages: [...this.loadedPages].sort((a, b) => a - b),
          focusedProductId,
          focusedProductHandle,
          scrollY,
          savedAt: Date.now(),
        };
        try {
          sessionStorage.setItem(storageKeyFor(this.collectionUrl, normalizedQuery), JSON.stringify(state));
        } catch (error) {
          console.warn('[collection-infinite-scroll] unable to persist navigation state', error);
        }
      }

      readSavedState() {
        const normalizedQuery = normalizeCollectionQuery(window.location.search);
        let raw = null;
        try {
          raw = sessionStorage.getItem(storageKeyFor(this.collectionUrl, normalizedQuery));
        } catch {
          return null;
        }
        if (!raw) return null;
        try {
          const parsed = JSON.parse(raw);
          if (!parsed || parsed.version !== STATE_VERSION) return null;
          if (parsed.collectionUrl !== this.collectionUrl) return null;
          if (parsed.normalizedQuery !== normalizedQuery) return null;
          if (Date.now() - parsed.savedAt > STATE_MAX_AGE_MS) return null;
          if (!Array.isArray(parsed.loadedPages) || !parsed.loadedPages.length) return null;
          return parsed;
        } catch {
          return null;
        }
      }

      clearSavedState() {
        const normalizedQuery = normalizeCollectionQuery(window.location.search);
        try {
          sessionStorage.removeItem(storageKeyFor(this.collectionUrl, normalizedQuery));
        } catch {
          /* ignore */
        }
      }

      async maybeRestore() {
        if (this._bfcacheRestored || this._restoreAttempted) return;
        const saved = this.readSavedState();
        if (!saved) return;
        this._restoreAttempted = true;

        const renderedPage = Number(this.dataset.currentPage) || 1;
        const needed = [...new Set(saved.loadedPages)]
          .map(Number)
          .filter((page) => page > renderedPage && page <= this.totalPages)
          .sort((a, b) => a - b)
          .slice(0, MAX_RESTORE_PAGES);

        const hasFocusTarget = Boolean(saved.focusedProductId || saved.focusedProductHandle);
        if (!needed.length && !hasFocusTarget && typeof saved.scrollY !== 'number') {
          this.clearSavedState();
          return;
        }

        this.restoring = true;
        this.abortActive();
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

        try {
          for (const page of needed) {
            const pageUrl = buildCollectionPageUrl(window.location.pathname, window.location.search, page);
            const ok = await this.loadNextPage({ reason: 'restore', pageUrl });
            if (!ok) break;
          }
          await this.restoreFocus(saved);
          this.clearSavedState();
        } finally {
          this.restoring = false;
          this.observeSentinel();
        }
      }

      async restoreFocus(saved) {
        const findCard = () => {
          if (saved.focusedProductId) {
            return this.grid.querySelector(
              `[data-product-card][data-product-id="${CSS.escape(saved.focusedProductId)}"]`
            );
          }
          if (saved.focusedProductHandle) {
            return this.grid.querySelector(
              `[data-product-card][data-product-handle="${CSS.escape(saved.focusedProductHandle)}"]`
            );
          }
          return null;
        };

        const waitFrames = (count = 2) =>
          new Promise((resolve) => {
            const step = (left) => {
              if (left <= 0) resolve();
              else requestAnimationFrame(() => step(left - 1));
            };
            step(count);
          });

        let card = findCard();
        if (card) {
          card.scrollIntoView({ block: 'center', behavior: 'auto' });
          await waitFrames(2);
          await Promise.race([
            Promise.all(
              [...card.querySelectorAll('img')].map((img) =>
                img.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      img.addEventListener('load', resolve, { once: true });
                      img.addEventListener('error', resolve, { once: true });
                    })
              )
            ),
            new Promise((resolve) => setTimeout(resolve, 500)),
          ]);
          card = findCard() || card;
          card.scrollIntoView({ block: 'center', behavior: 'auto' });
          return;
        }
        if (typeof saved.scrollY === 'number') {
          window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
        }
      }
    }
  );
}
